import React, { useState } from 'react'

import { ActionButton } from 'components'
import AdvTableSearchComponent from 'components/AdvancedTable/AdvTableSearchComponent'
import Box from 'components/Box'
import Table from 'components/Table'
import { SorterStatus } from 'components/Table/TableHeader'

import useModal from 'hooks/use-modal'
import { Pagination } from 'types/global'
import { useApiKeys } from 'queries/external-api'
import { ApiKey } from 'services/types/api-key'
import useUserPermissions from 'hooks/use-user-permissions'

import useApiKeysHooks from './hooks'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, DEFAULT_PAGINATION } from './helper'
import ApiKeyForm from './ApiKeyForm'

const ApiKeys: React.FC = () => {
  const { columns } = useApiKeysHooks()

  const [pagination, setPagination] = useState<Pagination<ApiKey>>(DEFAULT_PAGINATION)
  const [searchValue, setSearchValue] = useState('')
  const { setModal } = useModal()
  const [{ canCreate }] = useUserPermissions('externalApi')

  const { data, isLoading, isFetching, refetch } = useApiKeys({
    page: pagination.pageNumber ?? 1,
    per_page: pagination.pageSize ?? 100,
    sort_by: pagination.sortBy,
    ...(searchValue ? { query: searchValue } : {}),
  })

  const handleChangePagination = ({ pageNumber, pageSize }: Partial<Pagination<ApiKey>>) => {
    setPagination({ ...pagination, pageNumber: pageNumber ?? DEFAULT_PAGE, pageSize: pageSize ?? DEFAULT_PAGE_SIZE })
  }

  const handleChangeSorting = (sortKey: keyof ApiKey, order: SorterStatus) => {
    setPagination((prev) => ({
      ...prev,
      sortBy: order === 'default' ? undefined : { key: sortKey, order },
    }))
  }

  const handleSearch = (value: string) => {
    setSearchValue(value.trim())
    setPagination((prev) => ({ ...prev, pageNumber: DEFAULT_PAGE }))
  }

  const handleCreate = () => {
    setModal(
      <ApiKeyForm
        onClose={(success) => {
          success && refetch()
          setModal(null)
        }}
      />,
    )
  }

  return (
    <Box width="100%">
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap="8px" mb={16}>
        <AdvTableSearchComponent
          setSearchValue={handleSearch}
          value={searchValue}
          placeholder="external_api_key_search_placeholder"
        />
        {canCreate && (
          <ActionButton variant="create" onClick={handleCreate}>
            add_api_key_btn
          </ActionButton>
        )}
      </Box>
      <Table
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        isFetching={isFetching}
        isOnSearch={!!searchValue}
        onChangePagination={handleChangePagination}
        onSort={handleChangeSorting}
        pageNumber={pagination.pageNumber}
        pageSize={pagination.pageSize}
        totalDataCount={data?.total_count ?? 0}
        uniqueTableName="apiKeysTable"
        emptyText="external_api_key"
        isResponsiveHeight
        borderless
      />
    </Box>
  )
}

export default ApiKeys
